import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Switch } from '@multaqa/ui/components/ui/switch'
import { PencilIcon } from 'lucide-react'
import { useState } from 'react'
import { COMPANY_CURRENCIES } from '@/lib/company-currencies'
import type { GroupSettingsGroup, GroupUpdateInput } from './api'
import { useUpdateGroup } from './api'

type FormValues = {
  presentation_currency: string
  intercompany_mirroring: boolean
  retention_days: string
  purge_grace_days: string
}

function valuesFromGroup(group: GroupSettingsGroup): FormValues {
  return {
    presentation_currency: group.presentation_currency ?? '',
    intercompany_mirroring: group.intercompany_mirroring ?? false,
    retention_days: group.retention_days == null ? '' : String(group.retention_days),
    purge_grace_days: group.purge_grace_days == null ? '' : String(group.purge_grace_days),
  }
}

export function GroupSettings({ group }: { group: GroupSettingsGroup }) {
  const { t } = useTranslation()
  const update = useUpdateGroup()
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(() => valuesFromGroup(group))

  function startEditing() {
    setValues(valuesFromGroup(group))
    setEditing(true)
  }

  function cancel() {
    setEditing(false)
    setValues(valuesFromGroup(group))
  }

  function save() {
    const input: GroupUpdateInput = {
      presentation_currency: values.presentation_currency,
      intercompany_mirroring: values.intercompany_mirroring,
    }
    const retention = values.retention_days.trim()
    const purgeGrace = values.purge_grace_days.trim()

    if (retention) input.retention_days = Number(retention)
    if (purgeGrace) input.purge_grace_days = Number(purgeGrace)

    update.mutate(
      { groupId: group.id, input },
      {
        onSuccess: () => {
          setEditing(false)
          toast.success(t('groups.settings.form.saved'))
        },
      },
    )
  }

  return (
    <Panel data-testid="group-settings-panel">
      <PanelHeader>
        <PanelTitle>{t('groups.settings.title')}</PanelTitle>
        {editing ? (
          <div className="ms-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={cancel}
              disabled={update.isPending}
              data-testid="group-settings-cancel"
            >
              {t('groups.settings.form.cancel')}
            </Button>
            <Button
              type="submit"
              form="group-settings-form"
              size="sm"
              loading={update.isPending}
              disabled={update.isPending}
              data-testid="group-settings-save"
            >
              {t('groups.settings.form.save')}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={startEditing}
            data-testid="group-settings-edit"
          >
            <PencilIcon className="size-3.5" aria-hidden="true" />
            {t('groups.settings.view.edit')}
          </Button>
        )}
      </PanelHeader>
      <PanelContent className="p-4">
        {editing ? (
          <form
            id="group-settings-form"
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              save()
            }}
            data-testid="group-settings-form"
          >
            <CurrencyField
              value={values.presentation_currency}
              onChange={(presentation_currency) =>
                setValues((current) => ({ ...current, presentation_currency }))
              }
            />
            <MirroringField
              checked={values.intercompany_mirroring}
              onChange={(intercompany_mirroring) =>
                setValues((current) => ({ ...current, intercompany_mirroring }))
              }
            />
            <NumberField
              id="group-settings-retention-days"
              label={t('groups.settings.fields.retentionDays')}
              value={values.retention_days}
              onChange={(retention_days) =>
                setValues((current) => ({ ...current, retention_days }))
              }
            />
            <NumberField
              id="group-settings-purge-grace-days"
              label={t('groups.settings.fields.purgeGraceDays')}
              value={values.purge_grace_days}
              onChange={(purge_grace_days) =>
                setValues((current) => ({ ...current, purge_grace_days }))
              }
            />
          </form>
        ) : (
          <div className="grid gap-x-6 sm:grid-cols-2" data-testid="group-settings-view">
            <ReadOnlyField
              label={t('groups.settings.fields.presentationCurrency')}
              value={group.presentation_currency || '—'}
              data-testid="group-settings-value-presentation-currency"
            />
            <ReadOnlyField
              label={t('groups.settings.fields.intercompanyMirroring')}
              value={booleanLabel(t, group.intercompany_mirroring)}
              data-testid="group-settings-value-intercompany-mirroring"
            />
            <ReadOnlyField
              label={t('groups.settings.fields.retentionDays')}
              value={
                group.retention_days == null
                  ? t('groups.settings.platformDefault')
                  : String(group.retention_days)
              }
              data-testid="group-settings-value-retention-days"
            />
            <ReadOnlyField
              label={t('groups.settings.fields.purgeGraceDays')}
              value={
                group.purge_grace_days == null
                  ? t('groups.settings.platformDefault')
                  : String(group.purge_grace_days)
              }
              data-testid="group-settings-value-purge-grace-days"
            />
          </div>
        )}
      </PanelContent>
    </Panel>
  )
}

function CurrencyField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation()
  const id = 'group-settings-presentation-currency'

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-label text-muted-foreground font-mono uppercase">
        {t('groups.settings.fields.presentationCurrency')}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={id}
          data-testid={id}
          aria-label={t('groups.settings.fields.presentationCurrency')}
        >
          <SelectValue placeholder={t('groups.settings.fields.currencyPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {COMPANY_CURRENCIES.map((currency) => (
            <SelectItem key={currency} value={currency}>
              <span className="font-mono">{currency}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function MirroringField({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (value: boolean) => void
}) {
  const { t } = useTranslation()
  const id = 'group-settings-intercompany-mirroring'

  return (
    <div className="flex min-h-8 items-center justify-between gap-3 border-b border-border pb-2 sm:pb-0">
      <Label htmlFor={id} className="text-body text-start">
        {t('groups.settings.fields.intercompanyMirroring')}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={id} />
    </div>
  )
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-label text-muted-foreground font-mono uppercase">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min="1"
        step="1"
        numeric
        value={value}
        placeholder={t('groups.settings.platformDefault')}
        onChange={(event) => onChange(event.target.value)}
        data-testid={id}
      />
    </div>
  )
}

function ReadOnlyField({
  label,
  value,
  'data-testid': dataTestId,
}: {
  label: string
  value: string
  'data-testid': string
}) {
  return (
    <div className="border-b border-border py-3 last:border-b-0">
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      <div className="text-body text-foreground" data-testid={dataTestId}>
        {value}
      </div>
    </div>
  )
}
function booleanLabel(t: (key: string) => string, value: boolean | undefined): string {
  if (value === undefined) return '—'
  return value ? t('groups.settings.values.enabled') : t('groups.settings.values.disabled')
}
